import { CircularProgress, Grid, List, ListItemIcon, ListItemText, Typography } from "@mui/material"
import { RunStatusDto, useGetRunResultQuery } from "@store/services/runService"
import { format } from "date-fns"
import { useEffect, useState } from "react"
import ListItem from "@mui/material/ListItem"
import Button from "@mui/material/Button"
import RunErrorDialog, { Row } from "@features/scenarioDesign/RunTable/RunErrorDialog"
import { useLazyGetModelJsonQuery } from "@store/services/modelsService"
import { Link } from "react-router"
import { KeyboardArrowRight } from "@mui/icons-material"
import { CopyOnHover } from "@components/CopyOnHover"

type RunDetailsPanelProps = {
  runKey: string
}

const RunDetailsPanel = ({ runKey }: RunDetailsPanelProps) => {
  const { data, isLoading } = useGetRunResultQuery(runKey)
  const is_debug = import.meta.env.VITE_SHOW_DEBUG == "true"
  const [isRunGroupErrorDialogOpen, setIsRunGroupErrorDialogOpen] = useState(false)
  const [rows, setRows] = useState<Row[]>([])
  const [fetchModelJson, { data: json, isError: isFetchModelJsonError }] =
    useLazyGetModelJsonQuery()

  useEffect(() => {
    if (isFetchModelJsonError || !json) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    setRows(json as any[])
  }, [json, isFetchModelJsonError])

  useEffect(() => {
    if (!data) return

    if (![RunStatusDto.ERROR.toString()].includes(data.status)) return

    // we don't want to collect the model IF there is no such model yet
    if (data?.extra?.file) fetchModelJson(data.model_key)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [data?.status, data?.model_key])

  if (isLoading) {
    return (
      <Grid
        container
        item
        sx={{
          height: 70,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (!data) {
    return (
      <Grid
        container
        item
        sx={{
          height: 70,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid item>
          <Typography>
            ⚠️ <strong>Failed to load run details</strong>
          </Typography>
        </Grid>
      </Grid>
    )
  }

  const leftColumnRows: { label: string; value: string | React.ReactNode }[] = [
    { label: "Run ID", value: data.key },
    { label: "Created by", value: data.creator_email },
    { label: "Created", value: format(new Date(data.creation_date), "yyyy-MM-dd [HH:mm]") },
    { label: "Updated", value: format(new Date(data.last_updated), "yyyy-MM-dd [HH:mm]") },
    { label: "Model", value: data.model_key },
  ]
  const rightColumnRows = [
    { label: "Number of samples", value: data.num_samples },
    { label: "Seed", value: data.seed },
    { label: "Simulation Frequency", value: data.simulation_frequency },
    { label: "Rolling start month", value: data.yearly_rolling_start_month },
  ]

  if (data.linked_runs) {
    const linkedRuns = Object.entries(data.linked_runs).map(([graphKey, runGroupId]) => (
      <ListItem
        key={`${graphKey}_${runGroupId}`}
        sx={{ "& .MuiListItemIcon-root": { minWidth: 0 } }}
      >
        <ListItemIcon>
          <KeyboardArrowRight />
        </ListItemIcon>

        <Link
          reloadDocument
          to={`/ScenarioDesign?mode=runs&graphKey=${encodeURIComponent(
            graphKey,
          )}&runGroup=${runGroupId}`}
        >
          {graphKey}
        </Link>
      </ListItem>
    ))
    leftColumnRows.push({ label: "Linked Runs", value: <List>{linkedRuns}</List> })
  }

  const getTracebackMessage = (
    traceback: string | string[] | { message: string } | undefined,
  ): string | string[] => {
    if (!traceback) return ""
    if (Array.isArray(traceback)) return traceback
    if (typeof traceback === "string") return traceback

    return traceback.message
  }
  const tracebackMessage = getTracebackMessage(data?.extra?.traceback)

  return (
    <>
      <Grid container>
        <Grid item container direction="column" xs={6}>
          {leftColumnRows.map(({ label, value }) => (
            <Grid item container key={label}>
              <Grid item sx={{ minWidth: 120 }} xs={3}>
                <Typography component="span" sx={{ fontWeight: "bold" }}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <Typography sx={{ wordBreak: "break-all" }} component="span">
                  {value ? <CopyOnHover>{value}</CopyOnHover> : "-"}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>

        <Grid item container direction="column" xs={6}>
          {rightColumnRows.map(({ label, value }) => (
            <Grid item container key={label}>
              <Grid item sx={{ minWidth: 120 }} xs={3}>
                <Typography component="span" sx={{ fontWeight: "bold" }}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <Typography sx={{ wordBreak: "break-all" }} component="span">
                  {value ? <CopyOnHover>{value}</CopyOnHover> : "-"}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>

        {isRunGroupErrorDialogOpen && (
          <RunErrorDialog
            rows={rows}
            error={data?.extra?.message + " for " + data.run_name || "Unknown Error"}
            onClose={() => setIsRunGroupErrorDialogOpen(false)}
          />
        )}

        {data.status === RunStatusDto.TIMEOUT || data.status === RunStatusDto.ERROR ? (
          <Grid item container xs={12}>
            <Grid xs={12}>
              <Typography component="span" sx={{ fontSize: "18px", color: "red" }}>
                {data?.extra?.message || "Unknown Error"}
              </Typography>
            </Grid>
            <Grid xs={12}>
              {data?.extra?.task == "convert" && data?.extra?.file && (
                <Button
                  onClick={() => setIsRunGroupErrorDialogOpen(!isRunGroupErrorDialogOpen)}
                  variant={"contained"}
                >
                  View Diagnostic Conversion Error Table
                </Button>
              )}
            </Grid>
            <List>
              {Array.isArray(tracebackMessage) &&
                tracebackMessage.map((p: string) => (
                  <ListItem
                    divider
                    key={p}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(225, 225, 225, 0.4)",
                      },
                    }}
                  >
                    <ListItemText primary={p} />
                  </ListItem>
                ))}
              {!Array.isArray(tracebackMessage) && (
                <Typography sx={{ backgroundColor: "rgba(225, 225, 225, 0.4)" }}>
                  {tracebackMessage}
                </Typography>
              )}
            </List>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </>
  )
}

export default RunDetailsPanel
